import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';

const sampleProfiles = [
    {
        name: "Pete Flint",
        role: "General Partner @ NFX",
        location: "San Francisco, California, United States",
        followers: "178K followers",
        bio: "Pete Flint is a General Partner at NFX, where he leads early-stage investments in applied AI...",
        avatar: "/assets/profile-display-photo-1.jpeg"
      },
      {
        name: "Aaref Hilaly",
        role: "Partner at Bain Capital Ventures",
        location: "San Francisco, California, United States",
        followers: "286K followers",
        bio: "Bain Capital Ventures invest early (seed / Series A), and helps technical founders...",
        avatar: "/assets/profile-display-photo-2.jpeg"
      },
      {
        name: "Hemant Taneja",
        role: "CEO, General Catalyst",
        location: "Palo Alto, California, United States",
        followers: "80K followers",
        bio: "As an early investor in companies like Stripe, Samsara, Snap, and others, Hemant...",
        avatar: "/assets/profile-display-photo-3.jpeg"
      }      
];

const LinkedInProfile = ({ profile, index, opacity }) => {
  return (
    <div 
      className="transition-all duration-500 ease-out"
      style={{ opacity }}
    >
      {/* card visuals removed: transparent background, no border/shadow/rounded corners */}
      <div className="bg-transparent rounded-none shadow-none overflow-hidden border-0">
        <div className="p-2 md:p-6 flex gap-2 md:gap-4">
          <img 
            src={profile.avatar} 
            alt={profile.name}
            className="w-10 h-10 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-xs md:text-lg mb-0.5 md:mb-1 truncate">{profile.name}</h3>
            <p className="text-gray-700 text-[10px] md:text-sm mb-0.5 md:mb-1 line-clamp-1">{profile.role}</p>
            <p className="text-gray-500 text-[9px] md:text-sm mb-1 md:mb-2 truncate">{profile.location}</p>
            <div className="flex items-center gap-0.5 md:gap-1 text-gray-600 text-[9px] md:text-sm mb-1 md:mb-3">
              <svg className="w-2.5 h-2.5 md:w-4 md:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              <span className="truncate">{profile.followers}</span>
            </div>
            <p className="text-gray-800 text-[10px] md:text-sm leading-relaxed line-clamp-2 md:line-clamp-none">{profile.bio}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LynkrDemoSection = () => {
  const [animationState, setAnimationState] = useState('idle');

  useEffect(() => {
    let timeouts = [];

    const runAnimation = () => {
      // Show user message
      timeouts.push(setTimeout(() => setAnimationState('user-message'), 500));
      
      // Show loading
      timeouts.push(setTimeout(() => setAnimationState('loading'), 2000));
      
      // Show profiles
      timeouts.push(setTimeout(() => {
        setAnimationState('profiles');
      }, 3500));
      
      // Reset and loop
      timeouts.push(setTimeout(() => {
        setAnimationState('idle');
      }, 9000));
      
      // Start over
      timeouts.push(setTimeout(runAnimation, 10000));
    };

    runAnimation();

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <section className="py-4 md:py-8 px-2 md:px-6">
      <div className="max-w-7xl mx-auto">

        <div className="relative min-h-[400px] md:min-h-[700px]">
          {/* User Message - Top Right */}
          <div className="absolute top-0 right-0 max-w-[200px] md:max-w-md">
            <div 
              className={`transition-all duration-700 ${
                animationState !== 'idle' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="bg-white text-black rounded-2xl md:rounded-3xl rounded-tr-sm px-3 py-2 md:px-6 md:py-4 shadow-lg">
                <p className="text-xs md:text-lg">Who are some prominent AI startup investors in SF?</p>
              </div>
              <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 justify-end mr-2 md:mr-4">
                <span className="text-xs md:text-sm text-gray-500">You</span>
                <MessageCircle className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Response - Below User Message on Left */}
          <div className="absolute top-16 md:top-24 left-0 w-full max-w-3xl">
            {/* Loading State */}
            <div
              className={`absolute w-2/3 md:w-1/2 inset-x-0 top-24 md:top-28 z-10 transition-all duration-700 ${
                animationState === 'loading' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="bg-white rounded-2xl md:rounded-3xl rounded-tl-sm px-3 py-2 md:px-6 md:py-4 shadow-lg">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex gap-0.5 md:gap-1">
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-xs md:text-base text-gray-600">Searching for profiles...</span>
                </div>
              </div>
              <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 ml-2 md:ml-4">
                <span className="text-xs md:text-sm text-gray-500">Lynkr</span>
              </div>
            </div>

            {/* Profiles State */}
            <div
              className={`w-4/5 md:w-4/5 absolute inset-x-0 top-0 z-20 transition-all duration-700 ${
                animationState === 'profiles' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              {/* parent keeps the white rounded container; individual profile cards are now borderless/shadowless and stack with no gaps */}
              <div className="bg-white rounded-2xl md:rounded-3xl rounded-tl-sm px-3 py-3 md:px-6 md:py-6">
                <LinkedInProfile profile={sampleProfiles[0]} index={0} opacity={1} />
                <LinkedInProfile profile={sampleProfiles[1]} index={1} opacity={0.7} />
                <LinkedInProfile profile={sampleProfiles[2]} index={2} opacity={0.4} />
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default LynkrDemoSection;
